import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { addDays, addMonths, format } from 'date-fns'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import * as Mutation from '../../../api/gql/mutation'

interface Props {
  open: boolean
  onClose: () => void
  meeting: {
    id: string
    recurrence: 'NONE' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY'
    recurrenceUntil?: string | null
    scheduledAt: string
    occurrenceCount: number
  }
  onFinalized?: (finalizedOccurrenceNumber: number) => void
}

const computeNext = (scheduledAt: string, recurrence: string): Date | null => {
  const d = new Date(scheduledAt)
  switch (recurrence) {
    case 'WEEKLY':
      return addDays(d, 7)
    case 'BIWEEKLY':
      return addDays(d, 14)
    case 'MONTHLY':
      return addMonths(d, 1)
    case 'QUARTERLY':
      return addMonths(d, 3)
    default:
      return null
  }
}

const FinalizeOccurrenceDialog: React.FC<Props> = ({ open, onClose, meeting, onFinalized }) => {
  const { t } = useTranslation()
  const [finalizeMutation, { loading }] = useMutation(Mutation.FINALIZE_MEETING_OCCURRENCE)

  const nextOccurrenceNumber = meeting.occurrenceCount + 1
  const isRecurring = meeting.recurrence !== 'NONE'
  const nextDate = isRecurring ? computeNext(meeting.scheduledAt, meeting.recurrence) : null
  const pastRecurrenceUntil =
    !!nextDate && !!meeting.recurrenceUntil && nextDate > new Date(meeting.recurrenceUntil)
  const edgeCase = isRecurring && pastRecurrenceUntil

  const handleFinalize = async (scheduleNext?: boolean) => {
    const finalizedNumber = meeting.occurrenceCount + 1
    try {
      await finalizeMutation({
        variables: {
          id: meeting.id,
          scheduleNext: scheduleNext === undefined ? null : scheduleNext,
        },
      })
      toastr.success(t('Common_success'), t('Meeting_finalize_success'))
      onClose()
      onFinalized?.(finalizedNumber)
    } catch (err: any) {
      toastr.error(t('Common_error'), err?.message || t('Common_error'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isRecurring
              ? t('Meeting_finalize_title_recurring', { number: nextOccurrenceNumber })
              : t('Meeting_finalize_title_oneshot')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>{t('Meeting_finalize_immutable_warning')}</p>
          {isRecurring && !edgeCase && nextDate && (
            <p>{t('Meeting_finalize_next_planned', { date: format(nextDate, 'd. M. yyyy') })}</p>
          )}
          {edgeCase && (
            <>
              <p className="text-amber-700">
                {t('Meeting_finalize_past_until', {
                  defaultValue:
                    'Tato schůzka je poslední v sérii — další termín by spadl mimo nastavené „date to". Můžete sérii uzavřít a zůstat na této occurence, nebo naplánovat další konání (date to se automaticky posune).',
                })}
              </p>
              {nextDate && (
                <p className="text-xs">
                  {t('Meeting_finalize_proposed_next', {
                    date: format(nextDate, 'd. M. yyyy'),
                    defaultValue: 'Další termín by byl {{date}}',
                  })}
                </p>
              )}
            </>
          )}
          {!isRecurring && <p>{t('Meeting_finalize_oneshot_warning')}</p>}
          {isRecurring && <p>{t('Meeting_finalize_action_items_carry')}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 pt-4 border-t flex-wrap">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('Common_cancel')}
          </Button>
          {edgeCase ? (
            <>
              <Button variant="outline" onClick={() => handleFinalize(false)} disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('Meeting_finalize_close_series', { defaultValue: 'Uzavřít sérii' })}
              </Button>
              <Button onClick={() => handleFinalize(true)} disabled={loading || !nextDate}>
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('Meeting_finalize_schedule_next', {
                  defaultValue: 'Naplánovat další occurence',
                })}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleFinalize()} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('Meeting_finalize_confirm')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FinalizeOccurrenceDialog
